import React from "react";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import * as S from "../../../services";
import { T, TB, TC } from "../../../Constants";
import { InstallationMapper, InstallationMapperRef } from "./Extras";

export type BaseFormProps = {
    /** The plain datasource as it exists currently */
    datasource: T.DataSource;
    /** Show the form in a modal */
    popup?: boolean;
    /** Modal parameters */
    modal?: M.StyleModalProps;
    /** Callback after validation/cancellation */
    validate: (datasource?: T.DataSource) => void;
}

type Users = Awaited<ReturnType<typeof S.getUsers>>["data"];

const default_datasource = (datasource: T.DataSource) => {
    // No need for default values if the datasource is already saved
    if (datasource._id) return datasource;
    // Set the default frequency for a BTV datasource
    if (datasource.type === "documents") {
        if (datasource.source_type === "api_btv") {
            datasource.frequency = "1w";
            datasource.next_fetch = new Date().toISOString();
        }
    }
    return datasource;
}

const BaseForm: React.FC<BaseFormProps> = ({ validate, ...props }) => {
    const [context] = H.useRoots();
    const extra_fields_ref = React.useRef<InstallationMapperRef>(null);
    const [users, set_users, users_status] = H.useAsyncState<Users>([]);
    const [errors, set_errors] = React.useState<T.Errors<T.DataSource>>({});
    const [datasource, set_datasource] = React.useState(default_datasource(props.datasource));
    const user_options = React.useMemo(() => users.map(u => ({ label: u.data.name, value: u._id })), [users]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getUsers({ includeAdmin: true, includeSelf: true })
            .then(res => isSubscribed && set_users(res.data, "done"))
            .catch(() => isSubscribed && set_users([], "error"));
        return () => {
            isSubscribed = false;
            set_users([], "load");
        }
    }, [set_users]);

    const extra_fields = React.useMemo(() => {
        // The extra content to render
        let content: React.ReactNode = null;
        // Ask for a frequency of fetching and a time of next start
        let ask_frequency = false;

        if (datasource.type === "documents") {
            if (datasource.source_type === "api_btv") {
                ask_frequency = true;
                content = <InstallationMapper
                    context={context}
                    ref={extra_fields_ref}
                    datasource={datasource}
                    update={set_datasource}
                />;
            }
        }

        return { content, ask_frequency };
    }, [context, datasource]);

    const on_validate = React.useCallback(() => {
        const new_errors: T.Errors<T.DataSource> = {};
        const next_fetch = TB.getDate(datasource.next_fetch);
        const frequency = TB.splitFrequency(datasource.frequency);

        if (extra_fields.ask_frequency) {
            if (!frequency) new_errors.frequency = TC.GLOBAL_REQUIRED_FIELD;
            if (!next_fetch) new_errors.next_fetch = TC.GLOBAL_REQUIRED_FIELD;
        }
        if (!datasource.name) new_errors.name = TC.GLOBAL_REQUIRED_FIELD;

        const has_extra_fields_errors = extra_fields_ref.current?.has_errors?.();
        if (Object.keys(new_errors).length > 0 || has_extra_fields_errors) set_errors(new_errors);
        else {
            const dismount = M.renderLoader();
            // Save the datasource, and kick off the process to fetch external elements
            S.saveDataSource(datasource)
                .then(datasource => {
                    validate(datasource.data)
                    // Show an alert to let the user know the process is asynchronous from now on
                    M.renderAlert({ type: "info", message: TC.DATASOURCE_ASYNC_ELEM_FETCHING });
                })
                .catch(M.Alerts.updateError)
                .finally(dismount);
        }
    }, [datasource, validate]);

    const footer = React.useMemo(() => <C.Flex justifyContent="end" alignItems="center">
        <C.Button icon="save" text={TC.GLOBAL_SAVE} onClick={on_validate} />
    </C.Flex>, [on_validate]);

    return React.createElement(
        props.popup ? M.BlankModal : React.Fragment,
        props.popup ? {
            ...props.modal,
            footer: footer,
            onQuit: () => validate?.(null),
            size: props.modal?.size || "md",
            title: props.modal?.title || TC.DATASOURCE_GLOBAL_TITLE,
        } as M.BlankModalProps : null,
        <>
            <C.Form.TextField
                required
                error={errors.name}
                value={datasource.name}
                label={TC.DATASOURCE_NAME}
                onChange={value => set_datasource(p => ({ ...p, name: value }))}
            />

            <C.Form.Select
                multiple
                error={errors.users}
                options={user_options}
                value={datasource.users}
                typeahead={{ height: "md" }}
                loading={users_status === "load"}
                label={TC.DATASOURCE_USER_NOTIFY_TITLE}
                onChange={value => set_datasource(p => ({ ...p, users: value }))}
            />

            {extra_fields.ask_frequency && <BS.Row className="g-1">
                <BS.Col>
                    <C.Form.Frequency
                        noText
                        noReset
                        error={errors.frequency}
                        value={datasource.frequency}
                        label={TC.DATASOURCE_FETCH_FREQUENCY}
                        onChange={frequency => set_datasource(p => ({ ...p, frequency }))}
                    />
                </BS.Col>
                <BS.Col>
                    <C.Form.DateTime
                        enableTime
                        error={errors.next_fetch}
                        value={datasource.next_fetch}
                        label={TC.DATASOURCE_FETCH_NEXT_DATE}
                        onChange={next_fetch => set_datasource(p => ({ ...p, next_fetch }))}
                    />
                </BS.Col>
            </BS.Row>}

            {extra_fields.content}

            {!props.popup && footer}
        </>
    )
}

export default BaseForm;